import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../hooks/api";
import { API_URLS } from "../App";
import { Link, useLocation, useNavigate } from "react-router-dom";
import ListItems from "../components/CompoundManageListPage/ListItems";
import { useLayoutContext } from "../context/LayoutContext";
import {
  CircleAlert,
  Download,
  FolderSyncIcon,
  PackageX,
  Upload,
} from "lucide-react";
import { ManagementCompound } from "../model/ManagementCompound";

interface FilterParams {
  search: string;
  page: number;
  per_page: number;
  sort_by: string;
  sort_direction: "asc" | "desc";
  stock_status: string;
}

const CompoundManageListPage = () => {
  const { isAuthenticated } = useAuth();
  const { pathname } = useLocation();
  const {
    setError,
    setSuccess,
    setModalScreen,
    //    page,
    //    setPage,
  } = useLayoutContext();
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = React.useState<ManagementCompound[]>([]);
  const [filters, setFilters] = React.useState<FilterParams>({
    search: window.location.search.includes("search=")
      ? new URLSearchParams(window.location.search).get("search") || ""
      : "",
    page: window.location.search.includes("page=")
      ? parseInt(
          new URLSearchParams(window.location.search).get("page") || "1",
          10,
        )
      : 1,
    per_page: window.location.search.includes("per_page=")
      ? parseInt(
          new URLSearchParams(window.location.search).get("per_page") || "10",
        )
      : 10,
    sort_by: window.location.search.includes("sort_by=")
      ? new URLSearchParams(window.location.search).get("sort_by") ||
        "category_slug"
      : "category_slug",
    sort_direction: window.location.search.includes("sort_direction=")
      ? (new URLSearchParams(window.location.search).get("sort_direction") ||
          "desc") === "asc"
        ? "asc"
        : "desc"
      : "desc",
    stock_status: window.location.search.includes("stock_status=")
      ? new URLSearchParams(window.location.search).get("stock_status") || ""
      : "",
  });
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);

  const fetchCatalogData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post(API_URLS.COMPOUNDS + "/_search", {
        search_text: filters.search,
        page: filters.page,
        limit: filters.per_page,
        sort: `${filters.sort_by} ${filters.sort_direction}`,
        stock_status: filters.stock_status || null,
      });
      setData(response.data.results || []);
      setTotalPages(response.data.total_pages || 1);
      setTotalResults(response.data.total_results || 1);
      updateURLParams();
    } catch (err: any) {
      setError(err.message || "Failed to fetch catalog data");
      console.warn("Error fetching catalog data:", err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateURLParams = () => {
    const params = new URLSearchParams();
    if (filters.search) params.set("search", filters.search);
    params.set("page", filters.page.toString());
    params.set("per_page", filters.per_page.toString());
    params.set("sort_by", filters.sort_by);
    params.set("sort_direction", filters.sort_direction);
    if (filters.stock_status) params.set("stock_status", filters.stock_status);
    //navigate({ pathname, search: params.toString() });
    history.replaceState({}, "", `${pathname}?${params.toString()}`);
  };

  const refreshCatalogData = async () => {
    setLoading(true);
    setError(null);
    try {
      await api.post(API_URLS.COMPOUNDS + "/_actions/refresh", {});
      setSuccess("Catalog refreshed successfully.");
    } catch (err: any) {
      setError(err.message || "Failed to refresh catalog");
      console.warn("Error refreshing catalog:", err.message);
    } finally {
      fetchCatalogData();
      setLoading(false);
    }
  };

  const handleView = () => {
    history.replaceState(
      {
        data: data,
        totalPages: totalPages,
        totalResults: totalResults,
        filters: filters,
      },
      "Manage List Page",
    );
  };

  const handleEdit = () => {
    history.replaceState(
      {
        data: data,
        totalPages: totalPages,
        totalResults: totalResults,
        filters: filters,
      },
      "Manage List Page",
    );
  };

  const handleDelete = async (
    id: string,
    e?: React.MouseEvent<HTMLAnchorElement>,
  ) => {
    e?.preventDefault();
    if (window.confirm("Are you sure you want to delete this item?")) {
      try {
        await api.delete(`${API_URLS.COMPOUNDS}/${encodeURIComponent(id)}`);
        setSuccess("Item deleted successfully.");
        fetchCatalogData();
      } catch (err: any) {
        console.warn("Error deleting item:", err.message);
        setError("Failed to delete item.");
      }
    }
  };

  const handleRefreshCatalog = async () => {
    if (!isAuthenticated()) {
      setError("You must be logged in to refresh the catalog.");
      return;
    }
    if (confirm("Tem certeza que deseja atualizar o catálogo?")) {
      await refreshCatalogData();
    }
  };

  const handleExportCatalog = async () => {
    if (!isAuthenticated()) {
      setError("You must be logged in to export the catalog.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.get(API_URLS.COMPOUNDS + "/_export", {
        responseType: "blob",
      });

      // Create a download link and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const timestamp = new Date()
        .toISOString()
        .replace(/[:.]/g, "-")
        .slice(0, -5);
      link.setAttribute("download", `catalog_export_${timestamp}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSuccess("Catalog exported successfully.");
    } catch (err: any) {
      setError(err.message || "Failed to export catalog");
      console.warn("Error exporting catalog:", err.message);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchCatalogData();
  }, [filters]);

  const [isActionsOpen, setIsActionsOpen] = useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsActionsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!isAuthenticated()) {
    return <div>You must be logged in to edit items.</div>;
  }

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <div className="flex flex-col gap-6">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <span className="text-3xl font-bold text-gray-900 tracking-tight">
              Gestão do Catálogo
            </span>
            <p className="text-gray-500 mt-1">
              Gerencie seus compostos, preços e estoque.
            </p>
          </div>

          <div className="flex items-center gap-3">
            {/* Actions Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsActionsOpen(!isActionsOpen)}
                className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium rounded-lg transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Ações
                <svg
                  className={`w-4 h-4 ml-2 transition-transform ${isActionsOpen ? "rotate-180" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {isActionsOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 z-50 divide-y divide-gray-100 transform opacity-100 scale-100 transition-all duration-200 origin-top-right">
                  <div className="py-1">
                    <Link
                      to="/catalog/import"
                      className="group flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                      onClick={() => setIsActionsOpen(false)}
                    >
                      <Upload className="mr-3 h-4 w-4 text-gray-400 group-hover:text-gray-500" />
                      Importar Items
                    </Link>
                    <button
                      onClick={handleExportCatalog}
                      className="group flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                    >
                      <Download className="mr-3 h-4 w-4 text-gray-400 group-hover:text-gray-500" />
                      Exportar Catálogo
                    </button>
                    <button
                      onClick={handleRefreshCatalog}
                      className="group flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                    >
                      <FolderSyncIcon className="mr-3 h-4 w-4 text-gray-400 group-hover:text-gray-500" />
                      Atualizar Dados
                    </button>
                  </div>
                  <div className="py-1">
                    <Link
                      to="/catalog/show_components_mismatched_prices"
                      className="group flex w-full items-center px-4 py-2 text-sm text-amber-700 hover:bg-amber-50"
                      onClick={() => setIsActionsOpen(false)}
                    >
                      <CircleAlert className="mr-3 h-4 w-4 text-amber-500 group-hover:text-amber-600" />
                      Verificar Preços
                    </Link>
                    <Link
                      to="/catalog/show_components_out_of_stock"
                      className="group flex w-full items-center px-4 py-2 text-sm text-red-700 hover:bg-red-50"
                      onClick={() => setIsActionsOpen(false)}
                    >
                      <PackageX className="mr-3 h-4 w-4 text-red-500 group-hover:text-red-600" />
                      Verificar Stock (Substituir)
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Search and Filters Bar */}
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative max-w-2xl flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg
                  className="h-5 w-5 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg leading-5 bg-gray-50 placeholder-gray-500 focus:outline-none focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-all duration-200"
                placeholder="Pesquisar por nome, descrição ou categoria..."
                value={filters.search}
                onChange={(e) =>
                  setFilters({ ...filters, search: e.target.value, page: 1 })
                }
                autoFocus
              />
            </div>
            <select
              value={filters.stock_status}
              aria-label="Estado de Stock"
              onChange={(e) =>
                setFilters({
                  ...filters,
                  stock_status: e.target.value,
                  page: 1,
                })
              }
              className="block w-full sm:w-56 px-3 py-2.5 border border-gray-300 rounded-lg leading-5 bg-gray-50 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-all duration-200"
            >
              <option value="">Estado de Stock: Todos</option>
              <option value="last_units">Últimas Unidades (1-5)</option>
              <option value="out_of_stock">Indisponível (0)</option>
            </select>
          </div>
        </div>
        <div className="manage-page">
          <div className="overflow-auto">
            <ListItems
              data={data}
              loading={loading}
              filters={filters}
              setFilters={setFilters}
              totalPages={totalPages}
              totalResults={totalResults}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onView={handleView}
              setModalScreen={setModalScreen}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompoundManageListPage;
